import { useState, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import { Play, Pause, Volume2, VolumeX, X, SkipForward, Eye } from 'lucide-react';
import { API } from './shared';

function parseTimestamp(ts) {
  if (!ts) return 0;
  const parts = String(ts).split(':');
  if (parts.length === 3) {
    const [h, m, rest] = parts;
    const [s, ms] = (rest || '0').split('.');
    return parseInt(h) * 3600 + parseInt(m) * 60 + parseFloat(s) + (ms ? parseFloat(`0.${ms}`) : 0);
  }
  return parseFloat(ts) || 0;
}

function fmt(s) {
  if (!s || isNaN(s)) return '0:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}` : `${m}:${sec.toString().padStart(2, '0')}`;
}

export function EpisodePreview({ videoUrl, title, introStart, introEnd, outroStart, outroEnd, contentFilters, light, onClose }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [applyFilters, setApplyFilters] = useState(true);
  const [adminFilters, setAdminFilters] = useState([]);
  const [announcement, setAnnouncement] = useState(null);
  const [showSkipIntro, setShowSkipIntro] = useState(false);
  const [inOutro, setInOutro] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const announcedRef = useRef({});
  const filterMutedRef = useRef(false);

  useEffect(() => {
    fetch(`${API}/admin/filters`, { credentials: 'include' }).then(r => r.json()).then(d => setAdminFilters(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Load video (HLS or MP4)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;
    setVideoError(null);
    setPlaying(false);
    if (videoUrl.includes('.m3u8') && Hls.isSupported()) {
      const hls = new Hls({ startLevel: -1, capLevelToPlayerSize: true, enableWorker: true, abrEwmaDefaultEstimate: 4000000, startFragPrefetch: true, manifestLoadingTimeOut: 5000, manifestLoadingMaxRetry: 1 });
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_, data) => { if (data.fatal) setVideoError('Impossible de charger la vidéo'); });
      hlsRef.current = hls;
      return () => { hls.destroy(); hlsRef.current = null; };
    }
    video.src = videoUrl;
    video.load();
  }, [videoUrl]);

  const iS = introEnd ? (introStart ? parseTimestamp(introStart) : 0) : -1;
  const iE = introEnd ? parseTimestamp(introEnd) : -1;
  const oS = outroStart ? parseTimestamp(outroStart) : -1;
  const oE = outroEnd ? parseTimestamp(outroEnd) : (oS > 0 ? (duration || Number.MAX_SAFE_INTEGER) : -1);
  const zones = (contentFilters || []).map((f, i) => ({
    idx: i,
    start: parseTimestamp(f.start),
    end: parseTimestamp(f.end),
    filterId: f.filter_id || f.type,
    def: adminFilters.find(af => af.id === (f.filter_id || f.type)),
  })).filter(z => z.end > z.start && z.filterId);

  const onTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const t = video.currentTime;
    setCurrentTime(t);
    setShowSkipIntro(iE > 0 && t >= iS && t < iE);
    setInOutro(oS > 0 && oE > 0 && t >= oS && t <= oE);
    if (!applyFilters) return;
    for (const z of zones) {
      const key = `${z.filterId}_${z.start}`;
      if (t < z.start - 2 && announcedRef.current[key]) announcedRef.current[key] = false;
      if (t >= z.start - 2 && t < z.start && !announcedRef.current[key]) {
        announcedRef.current[key] = true;
        setAnnouncement(z.def ? `${z.def.name} (${z.def.action === 'mute' ? 'muet' : 'saut'})` : 'Filtre');
        setTimeout(() => setAnnouncement(null), 3000);
      }
      if (t >= z.start && t < z.end) {
        const action = z.def?.action || 'skip';
        if (action === 'mute') {
          if (!filterMutedRef.current) { video.muted = true; filterMutedRef.current = true; }
        } else {
          video.currentTime = z.end;
        }
        return;
      }
    }
    if (filterMutedRef.current) { videoRef.current.muted = muted; filterMutedRef.current = false; }
  };

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().then(() => setPlaying(true)).catch(() => setVideoError('Lecture impossible dans ce navigateur'));
    else { video.pause(); setPlaying(false); }
  }, []);

  const seekTo = (t) => { if (videoRef.current) { videoRef.current.currentTime = Math.max(0, t); setCurrentTime(t); } };
  const pct = (t) => duration > 0 ? Math.min(100, (t / duration) * 100) : 0;

  if (!videoUrl) {
    return <div className={`rounded-xl border p-6 text-center text-xs ${light ? 'bg-gray-50 border-gray-200 text-gray-400' : 'bg-white/3 border-white/5 text-white/30'}`} data-testid="ep-preview-no-video">Ajoutez d'abord une vidéo pour prévisualiser</div>;
  }

  return (
    <div className={`rounded-xl border overflow-hidden ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-black border-white/10'}`} data-testid="ep-preview">
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 ${light ? 'bg-gray-50 border-b border-gray-200' : 'bg-white/5 border-b border-white/5'}`}>
        <div className="flex items-center gap-2">
          <Eye size={13} className="text-[#E9641A]" />
          <span className={`text-xs font-semibold ${light ? 'text-gray-700' : 'text-white/80'}`}>Prévisualisation {title ? `— ${title}` : ''}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="ep-preview-apply-toggle">
            <input type="checkbox" checked={applyFilters} onChange={e => setApplyFilters(e.target.checked)} className="accent-[#E9641A] w-3 h-3" />
            <span className={`text-[10px] ${light ? 'text-gray-500' : 'text-white/50'}`}>Appliquer sauts & filtres</span>
          </label>
          {onClose && <button onClick={onClose} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'} data-testid="ep-preview-close"><X size={15} /></button>}
        </div>
      </div>

      {/* Video */}
      <div className="relative bg-black aspect-video max-h-[420px] w-full flex items-center justify-center">
        <video ref={videoRef} className="w-full h-full object-contain" onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
          onEnded={() => setPlaying(false)}
          onClick={togglePlay}
          onError={(e) => { const c = e.target?.error?.code; if (c && videoUrl && !videoUrl.includes('.m3u8')) setVideoError(c === 4 ? 'Format non supporté par ce navigateur' : 'Erreur de chargement vidéo'); }}
          playsInline
        />
        {videoError && <div className="absolute inset-0 flex items-center justify-center bg-black/70"><p className="text-red-400 text-xs" data-testid="ep-preview-error">{videoError}</p></div>}
        {!playing && !videoError && (
          <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center" data-testid="ep-preview-bigplay">
            <span className="w-14 h-14 rounded-full bg-black/60 border border-white/30 flex items-center justify-center hover:bg-[#E9641A]/80 transition-colors"><Play size={22} className="text-white ml-1" /></span>
          </button>
        )}
        {/* Announcement chip */}
        {announcement && (
          <div className="absolute top-3 left-3 px-3 py-1.5 bg-[#E9641A] text-white text-[11px] font-semibold rounded-lg animate-pulse" data-testid="ep-preview-announcement">Filtre à venir : {announcement}</div>
        )}
        {/* Skip intro button (live) */}
        {showSkipIntro && applyFilters && (
          <button onClick={() => seekTo(iE)} className="absolute bottom-14 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-white text-black text-[11px] font-bold rounded hover:bg-white/90" data-testid="ep-preview-skip-intro">
            <SkipForward size={12} /> Passer l'intro
          </button>
        )}
        {inOutro && (
          <div className="absolute bottom-14 left-3 px-2.5 py-1 bg-purple-600/90 text-white text-[10px] font-semibold rounded" data-testid="ep-preview-outro-badge">Zone outro — "Épisode suivant" s'affichera ici</div>
        )}
      </div>

      {/* Controls + timeline with zone markers */}
      <div className={`px-3 py-2.5 ${light ? 'bg-gray-50' : 'bg-[#111]'}`}>
        <div className="relative h-2 rounded-full cursor-pointer group mb-2"
          style={{ background: light ? '#e5e7eb' : 'rgba(255,255,255,0.12)' }}
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seekTo(((e.clientX - r.left) / r.width) * duration); }}
          data-testid="ep-preview-timeline">
          {/* Intro zone */}
          {iE > 0 && duration > 0 && <div className="absolute h-full bg-blue-500/70 rounded-sm" style={{ left: `${pct(iS)}%`, width: `${pct(iE) - pct(iS)}%` }} title={`Intro ${fmt(iS)} → ${fmt(iE)}`} />}
          {/* Outro zone */}
          {oS > 0 && oE > 0 && duration > 0 && <div className="absolute h-full bg-purple-500/70 rounded-sm" style={{ left: `${pct(oS)}%`, width: `${pct(oE) - pct(oS)}%` }} title={`Outro ${fmt(oS)} → ${fmt(oE)}`} />}
          {/* Filter zones */}
          {zones.map(z => duration > 0 && (
            <div key={z.idx} className="absolute h-full bg-red-500/80 rounded-sm" style={{ left: `${pct(z.start)}%`, width: `${Math.max(0.5, pct(z.end) - pct(z.start))}%` }} title={`${z.def?.name || 'Filtre'} ${fmt(z.start)} → ${fmt(z.end)}`} />
          ))}
          {/* Progress */}
          <div className="absolute h-full bg-[#E9641A] rounded-full pointer-events-none" style={{ width: `${pct(currentTime)}%`, opacity: 0.9 }} />
          <div className="absolute w-3 h-3 bg-[#E9641A] rounded-full -top-0.5 pointer-events-none" style={{ left: `calc(${pct(currentTime)}% - 6px)` }} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={togglePlay} className={light ? 'text-gray-700 hover:text-black' : 'text-white/80 hover:text-white'} data-testid="ep-preview-play-btn">{playing ? <Pause size={16} /> : <Play size={16} />}</button>
            <button onClick={() => { const v = videoRef.current; if (v) { v.muted = !muted; setMuted(!muted); } }} className={light ? 'text-gray-500 hover:text-black' : 'text-white/50 hover:text-white'} data-testid="ep-preview-mute-btn">{muted ? <VolumeX size={15} /> : <Volume2 size={15} />}</button>
            <span className={`text-[10px] font-mono ${light ? 'text-gray-500' : 'text-white/50'}`}>{fmt(currentTime)} / {fmt(duration)}</span>
          </div>
          {/* Quick test jumps */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {iE > 0 && <button onClick={() => seekTo(Math.max(0, iS > 0 ? iS - 3 : 0))} className="px-2 py-0.5 text-[9px] font-semibold rounded bg-blue-500/15 text-blue-400 hover:bg-blue-500/30" data-testid="ep-preview-test-intro">Tester intro</button>}
            {oS > 0 && <button onClick={() => seekTo(Math.max(0, oS - 3))} className="px-2 py-0.5 text-[9px] font-semibold rounded bg-purple-500/15 text-purple-400 hover:bg-purple-500/30" data-testid="ep-preview-test-outro">Tester outro</button>}
            {zones.map(z => (
              <button key={z.idx} onClick={() => seekTo(Math.max(0, z.start - 4))} className="px-2 py-0.5 text-[9px] font-semibold rounded bg-red-500/15 text-red-400 hover:bg-red-500/30" data-testid={`ep-preview-test-filter-${z.idx}`}>Tester {z.def?.name || `filtre ${z.idx + 1}`}</button>
            ))}
          </div>
        </div>
        {/* Legend */}
        {(iE > 0 || oS > 0 || zones.length > 0) && (
          <div className="flex items-center gap-3 mt-1.5">
            {iE > 0 && <span className="flex items-center gap-1 text-[9px] text-blue-400"><span className="w-2 h-2 rounded-sm bg-blue-500/70 inline-block" />Intro (saut)</span>}
            {oS > 0 && <span className="flex items-center gap-1 text-[9px] text-purple-400"><span className="w-2 h-2 rounded-sm bg-purple-500/70 inline-block" />Outro</span>}
            {zones.length > 0 && <span className="flex items-center gap-1 text-[9px] text-red-400"><span className="w-2 h-2 rounded-sm bg-red-500/80 inline-block" />Filtres ({zones.length})</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* Fullscreen modal wrapper for previewing from the episodes list */
export function EpisodePreviewModal({ episode, onClose, light }) {
  if (!episode) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="ep-preview-modal">
      <div className="w-full max-w-3xl" onClick={e => e.stopPropagation()}>
        <EpisodePreview
          videoUrl={episode.video_url}
          title={episode.title || `Épisode ${episode.episode}`}
          introStart={episode.intro_start} introEnd={episode.intro_end}
          outroStart={episode.outro_start} outroEnd={episode.outro_end}
          contentFilters={episode.content_filters}
          light={false}
          onClose={onClose}
        />
      </div>
    </div>
  );
}
